// @flow

import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Callout, Intent } from '@blueprintjs/core';

import CategorizedBulkBar from '../pills/categorized-bulk-bar';
import CategorizedBulkPickerDialog from '../pills/categorized-bulk-picker-dialog';
import CategorizedPill from '../pills/categorized-pill';
import type {
  CategoryMeta,
  Option,
} from '../pills/categorized-picker-popover';

import {
  PREFERENCE_TYPES,
  findPreferenceTypeMeta,
} from './preference-types';
import type { UserProfilePermissionsT } from './use-user-profile-permissions';

import * as AuthAPI from '../../services/auth-api';
import { fetchAllUsersWithIncludes } from '../../actions/users';
import { errorToast, successToast } from '../../utils/toaster';
import * as Selectors from '../../selectors';
import * as UserListModel from '../../models/user-list';

type Mode = 'add' | 'remove';

const FALLBACK_CATEGORY_COLOR = '#5c7080';

const BULK_COPY = {
  addTitle: 'Bulk add preference',
  removeTitle: 'Bulk remove preference',
  addConfirmButton: 'Add preference',
  removeConfirmButton: 'Remove preference',
  categorySectionLabel: 'Preference type',
};

type BulkBarProps = {
  count: number,
  total: number,
  permissions: UserProfilePermissionsT,
  onAdd: () => void,
  onRemove: () => void,
  onClear: () => void,
  onSelectAll: () => void,
};

export const PreferencesBulkBar = ({
  count,
  total,
  permissions,
  onAdd,
  onRemove,
  onClear,
  onSelectAll,
}: BulkBarProps) => (
  <CategorizedBulkBar
    count={count}
    total={total}
    canBulkAdd={!!permissions && !!permissions.canBulkAdd}
    canBulkRemove={!!permissions && !!permissions.canBulkRemove}
    addLabel="Add preferences"
    removeLabel="Remove preferences"
    addDisabledTooltip="You do not have permission to add preferences."
    removeDisabledTooltip="You do not have permission to remove preferences."
    onAdd={onAdd}
    onRemove={onRemove}
    onClear={onClear}
    onSelectAll={onSelectAll}
  />
);

type DialogOwnProps = {
  isOpen: boolean,
  mode: Mode,
  selectedUsers: $ReadOnlyArray<UserListModel.userT>,
  onClose: () => mixed,
  onComplete: () => mixed,
};

type DialogStateProps = {
  locations: Object[],
  entityTags: Object,
  organisationId: ?string,
};

type DialogDispatchProps = {
  refetchAllUsers: () => Promise<void>,
};

type DialogProps = DialogOwnProps & DialogStateProps & DialogDispatchProps;

const PreferencesBulkDialogImpl = (props: DialogProps) => {
  const {
    isOpen,
    mode,
    selectedUsers,
    onClose,
    onComplete,
    locations,
    entityTags,
    organisationId,
    refetchAllUsers,
  } = props;

  const deps = useMemo(
    () => ({ locations, entityTags }),
    [locations, entityTags],
  );

  const categories: $ReadOnlyArray<CategoryMeta> = useMemo(
    () =>
      PREFERENCE_TYPES.map((meta) => ({
        key: meta.key,
        label: meta.label,
        color: meta.color || FALLBACK_CATEGORY_COLOR,
      })),
    [],
  );

  const options: $ReadOnlyArray<Option> = useMemo(
    () =>
      PREFERENCE_TYPES.flatMap((meta) =>
        meta.getOptions(deps).map((o) => ({
          id: o.id,
          label: o.label,
          categoryKey: meta.key,
          subTypeKey: o.subTypeKey,
          subTypeLabel: o.subTypeLabel,
          color: o.color,
        })),
      ),
    [deps],
  );

  const singleSelectCategories = useMemo(
    () =>
      PREFERENCE_TYPES.filter((m) => m.cardinality === 'single').map(
        (m) => m.key,
      ),
    [],
  );

  const count = selectedUsers.length;
  const userPlural = count === 1 ? 'user' : 'users';

  const renderConfirmation = (option: Option, n: number, m: Mode) => {
    const meta = findPreferenceTypeMeta(option.categoryKey);
    if (!meta) return null;
    const synthetic = {
      id: 0,
      userId: 0,
      orgId: organisationId || '',
      preferenceType: meta.key,
      preferenceValue: option.id,
    };
    const content = meta.buildPillContent(synthetic, deps);
    const plural = n === 1 ? 'user' : 'users';

    if (m === 'add') {
      const replaceCount =
        meta.cardinality === 'single'
          ? selectedUsers.filter(
              (u) => meta.getUserValuesForType(u).length > 0,
            ).length
          : 0;
      const duplicateCount =
        meta.cardinality === 'multi'
          ? selectedUsers.filter((u) =>
              meta.getUserValuesForType(u).includes(option.id),
            ).length
          : 0;
      return (
        <div>
          <p>
            This will add{' '}
            <CategorizedPill
              typeLabel={content.typeLabel}
              valueLabel={content.valueLabel}
              color={content.color}
              missing={content.missing}
              size="sm"
            />{' '}
            to {n} {plural}.
          </p>
          {replaceCount > 0 && (
            <Callout intent={Intent.WARNING} icon="info-sign">
              {replaceCount} of {n} selected {plural} already have a{' '}
              {meta.label} preference and will have it replaced.
            </Callout>
          )}
          {duplicateCount > 0 && (
            <Callout intent={Intent.NONE} icon="info-sign">
              {duplicateCount} of {n} selected {plural} already have this
              preference and will be skipped.
            </Callout>
          )}
        </div>
      );
    }
    const hasCount = selectedUsers.filter((u) =>
      meta.getUserValuesForType(u).includes(option.id),
    ).length;
    const skipCount = n - hasCount;
    return (
      <div>
        <p>
          This will remove{' '}
          <CategorizedPill
            typeLabel={content.typeLabel}
            valueLabel={content.valueLabel}
            color={content.color}
            missing={content.missing}
            size="sm"
          />
          .
        </p>
        <p>
          {hasCount} of {n} selected {plural} have this preference and will
          have it removed. {skipCount} {skipCount === 1 ? 'user' : 'users'}{' '}
          will be skipped.
        </p>
      </div>
    );
  };

  const handleConfirm = async (option: Option) => {
    const meta = findPreferenceTypeMeta(option.categoryKey);
    if (!meta) return;
    if (!meta.validateValue(option.id)) {
      errorToast({
        message: `Invalid value for ${meta.label}.`,
        intent: Intent.WARNING,
      });
      return;
    }
    if (!organisationId) {
      errorToast({
        message: 'Could not resolve organisation.',
        intent: Intent.WARNING,
      });
      return;
    }
    const body = {
      orgId: organisationId,
      userIds: selectedUsers.map((u) => u.id),
      preferences: [
        {
          preferenceType: meta.key,
          preferenceValue: option.id,
        },
      ],
    };

    try {
      const response =
        mode === 'add'
          ? await AuthAPI.bulkUpsertUserPreferences(body)
          : await AuthAPI.bulkDeleteUserPreferences(body);
      const errors =
        (response && Array.isArray(response.errors) && response.errors) || [];
      if (errors.length > 0) {
        const okCount = count - errors.length;
        errorToast({
          message: `Updated ${okCount} of ${count} ${userPlural}. ${errors.length} ${errors.length === 1 ? 'user' : 'users'} could not be updated.`,
          intent: Intent.WARNING,
        });
        // eslint-disable-next-line no-console
        console.warn('Bulk preference partial failure', errors);
      } else {
        successToast({
          message: `Updated ${count} ${userPlural}.`,
          intent: Intent.SUCCESS,
        });
      }
      // Decision §10·12: full refetch from the slice's canonical source.
      refetchAllUsers();
      onComplete();
    } catch (e) {
      errorToast({
        message: 'Bulk update failed. Please try again.',
        intent: Intent.DANGER,
      });
      // Re-throw so the dialog keeps itself open and the user can retry
      // without re-staging the preference.
      throw e;
    }
  };

  return (
    <CategorizedBulkPickerDialog
      isOpen={isOpen}
      mode={mode}
      selectionCount={count}
      categories={categories}
      options={options}
      singleSelectCategories={singleSelectCategories}
      copy={BULK_COPY}
      renderConfirmation={renderConfirmation}
      onClose={onClose}
      onConfirm={handleConfirm}
    />
  );
};

const mapStateToProps = (state): DialogStateProps => {
  const userModel = Selectors.getUserModel(state);
  return {
    locations: Selectors.getLocations(state),
    entityTags: Selectors.getEntityTagsState(state),
    organisationId: userModel ? userModel.organisationId : null,
  };
};

const mapDispatchToProps = {
  refetchAllUsers: fetchAllUsersWithIncludes,
};

export const PreferencesBulkDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)(PreferencesBulkDialogImpl);
